import React, { useEffect, useState } from 'react';
import { Alert, AlertTitle, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { optionsService } from '../../services/optionsService';

interface BudgetShortfall {
  strategy: string | null;
  maxLossPerLot: number;
  limit: number;
}

const BUDGET_PATTERN = /too large for risk budget: 1 lot max loss ₹([\d,.]+) > limit ₹([\d,.]+)/;
const toNumber = (s: string) => Number(s.replace(/,/g, ''));

/**
 * Tells the viewer when the engine's latest decision for their own account was "too large
 * for risk budget": one lot of the setup risks more than their per-position limit. The limit
 * is the user's choice, so this only explains it and points to Settings — it never changes it.
 */
export const RiskBudgetNotice: React.FC<{ showSettingsLink?: boolean }> = ({ showSettingsLink = true }) => {
  const [shortfall, setShortfall] = useState<BudgetShortfall | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      optionsService
        .getEngineLogs()
        .then((res) => {
          if (cancelled) return;
          // Rows come newest first; only the latest outcome for this account matters.
          const latest = res.rows.find(
            (r) => r.accountId === res.viewerAccountId && ['OPENED', 'REJECTED', 'NO_SIGNAL', 'CAP_REACHED', 'ERROR'].includes(r.category)
          );
          const match = latest?.message.match(BUDGET_PATTERN);
          setShortfall(
            match ? { strategy: latest!.strategy, maxLossPerLot: toNumber(match[1]), limit: toNumber(match[2]) } : null
          );
        })
        .catch(() => !cancelled && setShortfall(null));

    load();
    const timer = setInterval(load, 60000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  if (!shortfall) return null;

  const needed = Math.ceil(shortfall.maxLossPerLot / 500) * 500;

  return (
    <Alert
      severity="warning"
      sx={{ mb: 2 }}
      action={
        showSettingsLink ? (
          <Button component={RouterLink} to="/options/settings" color="inherit" size="small">
            Settings
          </Button>
        ) : undefined
      }
    >
      <AlertTitle sx={{ fontWeight: 700 }}>Your risk limit is too small for one lot</AlertTitle>
      The engine found a {shortfall.strategy ? shortfall.strategy.replace(/_/g, ' ').toLowerCase() : 'trade'} setup for
      your account but skipped it: one lot can lose up to <b>₹{shortfall.maxLossPerLot.toLocaleString('en-IN')}</b>, above
      your per-position limit of <b>₹{shortfall.limit.toLocaleString('en-IN')}</b>. To let it trade, raise “Max Position
      Risk” to at least <b>₹{needed.toLocaleString('en-IN')}</b> — only if that suits your own risk tolerance.
    </Alert>
  );
};
